import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { Button, Input, Modal, Select, Tag, DatePicker } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { userSelector } from '../../store/user/slice';
import { expense, expenseCategory, initExpense, DEFAULT_EXPENSE_CATEGORIES } from '../../model/expense';
import { api_expense, api_expense_category } from '../../api/expense';
import { api_sale } from '../../api/sale';
import { isDateInRange } from '../../utils/date-filter-utils';
import { api_payment_type } from '../../api/sale';
import { paymentType } from '../../model/master';
import { formatNumber, isEmpty, exportExcel } from '../../utils/util';
import CommonTableList from '../common/common-table-list';
import CommonDetail from '../common/common-detail';
import ExpenseDetail from './expense-detail';
import ExpenseCategoryModal from './expense-category-modal';
import ExpenseVoucherPrint from './expense-voucher-print';
import { printExpenseSummaryDocument } from '../../utils/record-print-utils';
import { decode as base64_decode } from 'js-base64';
import dayjs from 'dayjs';
import '../../assets/css/expense.css';
import { deleteFailureMessage } from '../../utils/write-result';

const { Option } = Select;

const Expense: React.FC = () => {
    const navigate = useNavigate();
    const user = useSelector(userSelector);

    // Data States
    // Phase 7C-6C follow-up: this started as SEED_EXPENSES -- five invented
    // expenses, four of them "recorded by" a manager -- painted on the first
    // render and only replaced once the API answered with a non-empty
    // list. On an empty database, or on any error, they never went away.
    const [data, setData] = useState<expense[]>([]);
    // Loading / ready / error for the expense list itself. Without it the
    // total, profit and top-category cards read ฿0 and "ไม่มีข้อมูล"
    // during the first fetch -- findings about the shop, stated before
    // the shop had answered.
    const [dataState, setDataState] = useState<'loading' | 'ready' | 'error'>('loading');
    // Real sales from /sale, for the income card.
    //
    // Three states, not two. `null` used to mean BOTH "still loading" and
    // "failed", so during the first fetch the card asserted the sales
    // system was not connected -- a sentence that was not true yet. A
    // screen must not answer a question it has not finished asking.
    const [salesLoad, setSalesLoad] =
        useState<{ state: 'loading' } | { state: 'ready'; rows: any[] } | { state: 'error' }>({ state: 'loading' });
    const [categories, setCategories] = useState<expenseCategory[]>(DEFAULT_EXPENSE_CATEGORIES);
    const [paymentTypes, setPaymentTypes] = useState<paymentType[]>([]);

    // Detail Form State
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [formData, setFormData] = useState<expense>(initExpense);

    // Filter States
    const [period, setPeriod] = useState<'all' | 'today' | '7days' | 'this_month' | 'last_month' | 'custom'>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<number | 'all'>('all');
    const [paymentFilter, setPaymentFilter] = useState<number | 'all'>('all');
    const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');

    // Modals
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
    const [voucherData, setVoucherData] = useState<expense>(initExpense);

    const filterField = ['doc_no', 'title', 'payee', 'recorded_by', 'category_name', 'remark'];
    const placeholder_txt = 'ค้นหาตามเลขที่เอกสาร, ชื่อรายการค่าใช้จ่าย, ผู้รับเงิน, หรือผู้บันทึก...';

    const loadData = async () => {
        try {
            const expRes = await api_expense('LIST', {});
            // Always replace, even with an empty list: an empty shop has no
            // expenses, and must not be shown somebody else's.
            setData(Array.isArray(expRes)
                ? expRes.map((item: any, idx: number) => ({ ...item, key: idx + 1 }))
                : []);
            setDataState('ready');

            // Income is fetched on its own so a sales failure cannot blank
            // the expense list.
            try {
                const saleRes = await api_sale('LIST', {});
                setSalesLoad({ state: 'ready', rows: Array.isArray(saleRes) ? saleRes : [] });
            } catch (e) {
                setSalesLoad({ state: 'error' });
                console.error(e);
            }

            const catRes = await api_expense_category('LIST', {});
            if (Array.isArray(catRes) && catRes.length > 0) {
                setCategories(catRes);
            }

            const payRes = await api_payment_type('LIST', {});
            if (Array.isArray(payRes) && payRes.length > 0) {
                setPaymentTypes(payRes);
            }
        } catch (e) {
            // A failed load shows an empty table, not a remembered one. The
            // 401 case is handled by the auth interceptor before this runs.
            setData([]);
            setDataState('error');
            console.error(e);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtered Data based on Period, Category, Payment Type, and Status
    const filteredData = useMemo(() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
        const thisMonthPrefix = `${currentYear}-${currentMonth}`;

        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthPrefix = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const sevenDaysStr = sevenDaysAgo.toISOString().slice(0, 10);

        return data.filter(item => {
            // Period
            if (period === 'today' && (!item.expense_date || !item.expense_date.startsWith(todayStr))) {
                return false;
            }
            if (period === '7days' && (!item.expense_date || item.expense_date < sevenDaysStr)) {
                return false;
            }
            if (period === 'this_month' && (!item.expense_date || !item.expense_date.startsWith(thisMonthPrefix))) {
                return false;
            }
            if (period === 'last_month' && (!item.expense_date || !item.expense_date.startsWith(lastMonthPrefix))) {
                return false;
            }
            if (period === 'custom') {
                const itemDate = item.expense_date ? item.expense_date.slice(0, 10) : '';
                if (startDate && itemDate < startDate) {
                    return false;
                }
                if (endDate && itemDate > endDate) {
                    return false;
                }
            }

            // Category
            if (categoryFilter !== 'all' && item.category_id !== categoryFilter) {
                return false;
            }

            // Payment Type
            if (paymentFilter !== 'all' && item.payment_type_id !== paymentFilter) {
                return false;
            }

            // Status
            if (statusFilter !== 'all' && item.status !== statusFilter) {
                return false;
            }

            return true;
        });
    }, [data, period, startDate, endDate, categoryFilter, paymentFilter, statusFilter]);

    // KPI Metrics
    const kpiMetrics = useMemo(() => {
        const activeExpenses = data.filter(x => x.status === 1);
        const totalAmount = activeExpenses.reduce((acc, curr) => acc + Number(curr.net_amount || 0), 0);

        const now = new Date();
        const thisMonthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const todayStr = now.toISOString().slice(0, 10);

        const thisMonthExpenses = activeExpenses.filter(x => x.expense_date && x.expense_date.startsWith(thisMonthPrefix));
        const thisMonthAmount = thisMonthExpenses.reduce((acc, curr) => acc + Number(curr.net_amount || 0), 0);

        const todayExpenses = activeExpenses.filter(x => x.expense_date && x.expense_date.startsWith(todayStr));
        const todayAmount = todayExpenses.reduce((acc, curr) => acc + Number(curr.net_amount || 0), 0);

        // Income: real sales from /sale, over the SAME period the expense
        // list is filtered to -- that is what makes "net profit" a number
        // that means something. Category / payment filters are properties
        // of expenses and do not apply to income. Until 2026-09-08 this was
        // the sum of hardcoded sample receipts, on every visit.
        const periodBounds = (): { start: string; end: string } => {
            if (period === 'today') return { start: todayStr, end: todayStr };
            if (period === '7days') {
                const d = new Date(); d.setDate(d.getDate() - 7);
                return { start: d.toISOString().slice(0, 10), end: '' };
            }
            if (period === 'this_month') return { start: `${thisMonthPrefix}-01`, end: `${thisMonthPrefix}-31` };
            if (period === 'last_month') {
                const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const prefix = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return { start: `${prefix}-01`, end: `${prefix}-31` };
            }
            if (period === 'custom') return { start: startDate || '', end: endDate || '' };
            return { start: '', end: '' };
        };
        const bounds = periodBounds();
        const salesInPeriod = salesLoad.state !== 'ready' ? null : salesLoad.rows.filter((s: any) =>
            (s.status === undefined || Number(s.status) === 1) &&
            isDateInRange(s.create_date, bounds.start, bounds.end));
        // Field precedence follows api/report.ts: a sale row's `price` is
        // the net bill total when no explicit net field is present.
        const totalIncome: number | null = salesInPeriod === null ? null :
            salesInPeriod.reduce((acc: number, s: any) =>
                acc + Number(s.total_price ?? s.net_price ?? s.price ?? 0), 0);
        const receiptsCount = salesInPeriod === null ? 0 : salesInPeriod.length;
        // Net profit is income minus expenses, so it inherits the worse
        // of the two states. Computing it while either side is unknown
        // would silently treat that side as zero.
        const profitState: 'loading' | 'ready' | 'error' =
            (salesLoad.state === 'error' || dataState === 'error') ? 'error'
                : (salesLoad.state === 'loading' || dataState === 'loading') ? 'loading'
                    : 'ready';
        const netProfit: number | null =
            profitState === 'ready' && totalIncome !== null ? totalIncome - totalAmount : null;

        // Top Category
        const catMap: { [key: number]: { name: string; total: number } } = {};
        activeExpenses.forEach(x => {
            if (!catMap[x.category_id]) {
                const found = categories.find(c => c.id === x.category_id);
                catMap[x.category_id] = { name: found ? found.name : x.category_name || 'ทั่วไป', total: 0 };
            }
            catMap[x.category_id].total += Number(x.net_amount || 0);
        });

        let topCatName = 'ไม่มีข้อมูล';
        let topCatAmount = 0;
        Object.values(catMap).forEach(c => {
            if (c.total > topCatAmount) {
                topCatAmount = c.total;
                topCatName = c.name;
            }
        });

        return {
            totalIncome,
            incomeState: salesLoad.state,
            expenseState: dataState,
            profitState,
            receiptsCount,
            netProfit,
            totalAmount,
            totalCount: activeExpenses.length,
            thisMonthAmount,
            thisMonthCount: thisMonthExpenses.length,
            todayAmount,
            todayCount: todayExpenses.length,
            topCatName,
            topCatAmount
        };
    }, [data, dataState, categories, salesLoad, period, startDate, endDate]);

    const getLoggedInUser = () => {
        let name = user?.name;
        let empId = user?.id;
        if (!name || name === '') {
            const uuid = typeof window !== 'undefined' ? window.localStorage.getItem("uuid") : null;
            if (uuid) {
                try {
                    const parsed = JSON.parse(base64_decode(uuid));
                    name = parsed.name || parsed.username || parsed.code || '';
                    empId = parsed.id || 1;
                } catch (e) {}
            }
        }
        // Phase 7C-6C: previously defaulted to the name 'ผู้จัดการ ร้านค้า'
        // and employee id 1. That named a real person, and claimed a real
        // employee row, for a request nobody had authenticated. Backend
        // 7C-5 stamps the true actor over whatever is sent, so sending
        // nothing is both honest and sufficient.
        return { name: name || '', id: empId };
    };

    const onShowDetail = (showMode: string, recordData?: expense) => {
        const currentUser = getLoggedInUser();

        if (showMode === 'ADD') {
            setTitle('บันทึกค่าใช้จ่ายใหม่ (New Expense)');
            setFormData({
                ...initExpense,
                doc_no: '',
                recorded_by: currentUser.name,
                employee_id: currentUser.id,
                category_id: categories.length > 0 ? categories[0].id : 1,
                category_name: categories.length > 0 ? categories[0].name : '',
            });
        } else if (showMode === 'COPY') {
            setTitle('บันทึกค่าใช้จ่าย (คัดลอกรายการ)');
            setFormData(recordData ? {
                ...recordData,
                id: 0,
                doc_no: '',
                title: `${recordData.title} (คัดลอก)`,
                recorded_by: currentUser.name,
                employee_id: currentUser.id,
            } : { ...initExpense });
        } else if (showMode === 'EDIT') {
            setTitle('แก้ไขรายการค่าใช้จ่าย');
            setFormData(recordData ? {
                ...recordData,
                recorded_by: recordData.recorded_by || currentUser.name,
                employee_id: recordData.employee_id || currentUser.id,
            } : { ...initExpense });
        } else if (showMode === 'VIEW') {
            setTitle('รายละเอียดค่าใช้จ่าย');
            setFormData(recordData ? { ...recordData } : { ...initExpense });
        }

        setMode(showMode);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        const body = { ...formData };

        if (body.id && body.id > 0 && mode === 'EDIT') {
            operation = 'UPDATE';
            key_id = body.id;
        }

        if (!isEmpty(body.title) && body.amount > 0) {
            body.title = body.title.trim();
            if (operation === 'INSERT') {
                delete (body as any).doc_no;
                delete (body as any).document_no;
                // Phase 7C-6C: 'คัดลอกรายการ' spreads the source record,
                // which carries its author's `create_by`. Backend prefers
                // a supplied `create_by` over the session-stamped actor,
                // so a copied expense was filed under the original
                // author's name instead of whoever copied it. Dropping it
                // lets the verified actor stand.
                delete (body as any).create_by;
            }
            body.status = body.status !== undefined ? body.status : 1;

            try {
                const res = await api_expense(operation, body, key_id);
                if (res === 1 || res?.status === 200 || res) {
                    setOpen(false);
                    Modal.success({
                        title: 'บันทึกสำเร็จ',
                        icon: <ExclamationCircleFilled rev={undefined} />,
                        content: operation === 'UPDATE' ? 'แก้ไขรายการค่าใช้จ่ายเรียบร้อยแล้ว' : 'บันทึกรายการค่าใช้จ่ายใหม่เรียบร้อยแล้ว',
                        centered: true,
                        onOk() { loadData(); }
                    });
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            Modal.error({
                title: 'ข้อมูลไม่ครบถ้วน',
                icon: <ExclamationCircleFilled rev={undefined} />,
                content: 'กรุณากรอกชื่อรายการค่าใช้จ่ายและระบุจำนวนเงินที่ถูกต้องมากกว่า 0 บาท',
                centered: true
            });
        }
    };

    const onDelete = (record: expense) => {
        Modal.confirm({
            title: 'ยืนยันการลบรายการ',
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: `คุณต้องการลบรายการค่าใช้จ่าย "${record.title}" (เอกสาร: ${record.doc_no}) หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้`,
            okText: 'ลบรายการ',
            cancelText: 'ยกเลิก',
            okButtonProps: { danger: true },
            centered: true,
            async onOk() {
                try {
                    await api_expense('DELETE', {}, record.id);
                    Modal.success({
                        title: 'ลบสำเร็จ',
                        content: 'ลบรายการค่าใช้จ่ายเรียบร้อยแล้ว',
                        centered: true,
                        onOk() { loadData(); }
                    });
                } catch (e) {
                    // This used to `console.error` and stop, so a failed delete
                    // told the user nothing at all -- the dialog simply closed
                    // and the row was still there after a refresh.
                    console.error(e);
                    Modal.error({
                        title: 'ผิดพลาด',
                        content: deleteFailureMessage(e, 'ลบรายการค่าใช้จ่ายไม่สำเร็จ'),
                        centered: true,
                    });
                }
            }
        });
    };

    const onOpenVoucher = (record: expense) => {
        setVoucherData(record);
        setIsVoucherModalOpen(true);
    };

    const handleExportExcel = () => {
        const exportList = filteredData.map((item, idx) => ({
            'ลำดับ': idx + 1,
            'เลขที่เอกสาร': item.doc_no,
            'วันที่จ่าย': item.expense_date,
            'หมวดหมู่': item.category_name || '-',
            'รายการค่าใช้จ่าย': item.title,
            'จ่ายให้แก่': item.payee || '-',
            'ช่องทางชำระเงิน': item.payment_type_name || 'เงินสด',
            'ยอดก่อนภาษี': Number(item.amount || 0),
            'ภาษีมูลค่าเพิ่ม VAT': Number(item.vat_amount || 0),
            'ภาษีหัก ณ ที่จ่าย WHT': Number(item.wht_amount || 0),
            'ยอดจ่ายสุทธิ': Number(item.net_amount || 0),
            'ผู้บันทึก': item.recorded_by || '-',
            'สถานะ': item.status === 1 ? 'ปกติ' : 'ยกเลิก',
            'หมายเหตุ': item.remark || '-'
        }));
        const periodStr = period === 'custom'
            ? `ช่วงวัน_${startDate || 'เริ่ม'}_ถึง_${endDate || 'สิ้นสุด'}`
            : period;
        exportExcel(`รายงานค่าใช้จ่าย_${periodStr}`, exportList);
    };

    const handlePrintExpenseReport = () => {
        const periodLabels: Record<string, string> = {
            all: 'ทั้งหมด',
            today: 'วันนี้',
            '7days': '7 วันล่าสุด',
            this_month: 'เดือนนี้',
            last_month: 'เดือนที่แล้ว'
        };
        const customPeriodText = startDate && endDate
            ? `${startDate} ถึง ${endDate}`
            : startDate
                ? `ตั้งแต่ ${startDate}`
                : endDate
                    ? `ถึง ${endDate}`
                    : 'กำหนดช่วงวัน';
        const periodText = period === 'custom' ? customPeriodText : (periodLabels[period] || 'ทั้งหมด');

        const catName = categoryFilter === 'all' 
            ? 'ทุกหมวดหมู่' 
            : (categories.find(c => c.id === categoryFilter)?.name || 'หมวดหมู่ที่เลือก');
        const payName = paymentFilter === 'all'
            ? 'ทุกช่องทาง'
            : (paymentFilter === 1 ? 'เงินสด' : paymentFilter === 2 ? 'เงินโอน' : paymentFilter === 3 ? 'QR PromptPay' : 'บัตรเครดิต');

        // Paper cannot be corrected by a later render. An expense report
        // printed mid-fetch would carry ฿0 totals; one printed while sales
        // are still loading would claim the income is unknown when it is
        // merely late. A failed SALES fetch is allowed through -- the
        // document says so in words.
        if (kpiMetrics.expenseState !== 'ready' || kpiMetrics.incomeState === 'loading') {
            Modal.warning({
                title: 'ยังพิมพ์รายงานไม่ได้',
                icon: <ExclamationCircleFilled rev={undefined} />,
                content: kpiMetrics.expenseState === 'error'
                    ? 'โหลดรายการค่าใช้จ่ายไม่สำเร็จ กรุณารีเฟรชหน้านี้แล้วลองใหม่'
                    : 'ข้อมูลกำลังโหลดอยู่ กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
                centered: true
            });
            return;
        }

        const currentUser = getLoggedInUser();

        printExpenseSummaryDocument({
            data: filteredData,
            kpi: kpiMetrics,
            periodText,
            categoryText: catName,
            paymentText: payName,
            userName: currentUser.name
        });
    };

    const columns: ColumnsType<expense> = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '65px',
            align: 'center',
            render: (_t, _r, index) => index + 1
        },
        {
            title: 'เลขที่เอกสาร',
            dataIndex: 'doc_no',
            key: 'doc_no',
            width: '130px',
            render: (doc_no) => (
                <Tag color="volcano" style={{ fontWeight: 700, fontSize: '13px', padding: '2px 8px', borderRadius: '6px' }}>
                    {doc_no || '-'}
                </Tag>
            )
        },
        {
            title: 'วันที่จ่าย',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: '115px',
            render: (date) => <span style={{ fontWeight: 600, fontSize: '13px' }}>{date || '-'}</span>
        },
        {
            title: 'รายการค่าใช้จ่าย & หมวดหมู่',
            dataIndex: 'title',
            key: 'title',
            render: (title, record) => {
                const cat = categories.find(c => c.id === record.category_id);
                return (
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'inherit', marginBottom: '4px' }}>
                            {title}
                        </div>
                        <div className="expense-cat-badge" style={{
                            background: cat?.color ? `${cat.color}18` : '#FF7A5918',
                            color: cat?.color || '#FF7A59',
                            border: `1px solid ${cat?.color ? `${cat.color}40` : '#FF7A5940'}`
                        }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cat?.color || '#FF7A59' }} />
                            <span>{record.category_name || cat?.name || 'ทั่วไป'}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'จ่ายให้แก่',
            dataIndex: 'payee',
            key: 'payee',
            width: '150px',
            render: (payee) => (
                <span style={{ color: 'var(--text-secondary, #64748B)', fontSize: '13px' }}>
                    {payee ? `🏢 ${payee}` : 'เงินสด / ทั่วไป'}
                </span>
            )
        },
        {
            title: 'ช่องทางชำระ',
            dataIndex: 'payment_type_name',
            key: 'payment_type_name',
            width: '140px',
            render: (name) => {
                const isCash = !name || name.includes('เงินสด') || name.includes('Cash');
                return (
                    <Tag color={isCash ? 'green' : 'blue'} style={{ borderRadius: '8px', fontWeight: 600, fontSize: '12px' }}>
                        {name || 'เงินสด (Cash)'}
                    </Tag>
                );
            }
        },
        {
            title: 'ยอดจ่ายสุทธิ',
            dataIndex: 'net_amount',
            key: 'net_amount',
            width: '130px',
            align: 'right',
            render: (net, record) => (
                <div>
                    <div style={{ fontWeight: 800, fontSize: '15px', color: '#FF7A59' }}>
                        ฿{formatNumber(net || record.amount || 0)}
                    </div>
                    {record.vat_amount > 0 && (
                        <div style={{ fontSize: '11px', color: '#64748B' }}>
                            VAT: +฿{formatNumber(record.vat_amount)}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: '110px',
            align: 'center',
            render: (status) => {
                const isActive = status === 1 || status === undefined;
                return (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: isActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                        color: isActive ? '#10B981' : '#EF4444'
                    }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isActive ? '#10B981' : '#EF4444' }} />
                        {isActive ? 'อนุมัติ' : 'ยกเลิก'}
                    </span>
                );
            }
        },
        {
            title: 'การดำเนินการ',
            dataIndex: 'operation',
            key: 'operation',
            width: '170px',
            fixed: 'right',
            render: (_, record) => (
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => onShowDetail('VIEW', record)} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ใบสำคัญจ่าย" onClick={() => onOpenVoucher(record)} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไขข้อมูล" onClick={() => onShowDetail('EDIT', record)} />
                    <FontAwesomeIcon icon="trash-can" title="ลบข้อมูล" onClick={() => onDelete(record)} />
                </div>
            )
        }
    ];

    return (
        <div className='expense-page-container' data-testid="expense-page">
            {/* Header & KPI Summary Cards */}
            <div className='expense-header-section'>
                <div className='expense-header-left'>
                    <div className='expense-header-icon'>
                        💰
                    </div>
                    <div>
                        <h2 className='expense-header-title'>บันทึกและจัดการค่าใช้จ่าย (Expense Management)</h2>
                        <span className='expense-header-subtitle'>
                            บันทึกรายจ่ายของร้าน แยกตามหมวดหมู่ คำนวณภาษี VAT/หัก ณ ที่จ่าย และพิมพ์ใบสำคัญจ่าย
                        </span>
                    </div>
                </div>
            </div>

            {/* KPI Cards Grid */}
            <div className='expense-kpi-grid'>
                <div className='expense-kpi-card income'>
                    <div className='expense-kpi-info'>
                        <span className='expense-kpi-label'>
                            <FontAwesomeIcon icon="store" style={{ color: '#10B981' }} /> รายรับ (ยอดขายหน้าร้าน)
                        </span>
                        <span className='expense-kpi-val income' data-testid="kpi-income-amount"
                            style={kpiMetrics.incomeState === 'ready' ? undefined : { color: '#94A3B8', fontSize: '16px' }}>
                            {kpiMetrics.incomeState === 'loading' ? 'กำลังโหลด…'
                                : kpiMetrics.incomeState === 'error' ? 'เชื่อมยอดขายไม่สำเร็จ'
                                    : `฿${formatNumber(kpiMetrics.totalIncome || 0)}`}
                        </span>
                        <span className='expense-kpi-sub'>
                            {kpiMetrics.incomeState === 'loading' ? 'กำลังดึงยอดขายจากระบบ'
                                : kpiMetrics.incomeState === 'error' ? 'ลองรีเฟรชหน้านี้อีกครั้ง'
                                    : `จากบิลขาย ${kpiMetrics.receiptsCount} ใบ (/sale)`}
                        </span>
                    </div>
                    <div className='expense-kpi-icon-box income'>🛒</div>
                </div>

                <div className='expense-kpi-card primary'>
                    <div className='expense-kpi-info'>
                        <span className='expense-kpi-label'>
                            <FontAwesomeIcon icon="coins" style={{ color: '#FF7A59' }} /> รายจ่ายรวมทั้งหมด
                        </span>
                        <span className='expense-kpi-val primary' data-testid="kpi-total-amount"
                            style={kpiMetrics.expenseState === 'ready' ? undefined : { color: '#94A3B8', fontSize: '16px' }}>
                            {kpiMetrics.expenseState === 'loading' ? 'กำลังโหลด…'
                                : kpiMetrics.expenseState === 'error' ? 'โหลดรายจ่ายไม่สำเร็จ'
                                    : `฿${formatNumber(kpiMetrics.totalAmount)}`}
                        </span>
                        <span className='expense-kpi-sub'>
                            {kpiMetrics.expenseState === 'loading' ? 'กำลังดึงรายการค่าใช้จ่าย'
                                : kpiMetrics.expenseState === 'error' ? 'ลองรีเฟรชหน้านี้อีกครั้ง'
                                    : `บันทึกแล้ว ${kpiMetrics.totalCount} รายการ`}
                        </span>
                    </div>
                    <div className='expense-kpi-icon-box primary'>💸</div>
                </div>

                <div className='expense-kpi-card profit'>
                    <div className='expense-kpi-info'>
                        <span className='expense-kpi-label'>
                            <FontAwesomeIcon icon="chart-line" style={{ color: '#0284C7' }} /> กำไรสุทธิ (Net Profit)
                        </span>
                        <span className='expense-kpi-val profit' data-testid="kpi-profit-amount"
                            style={kpiMetrics.profitState !== 'ready'
                                ? { color: '#94A3B8', fontSize: '16px' }
                                : { color: (kpiMetrics.netProfit || 0) >= 0 ? '#0284C7' : '#EF4444' }}>
                            {kpiMetrics.profitState === 'loading' ? 'กำลังโหลด…'
                                : kpiMetrics.profitState === 'error' ? 'คำนวณไม่ได้'
                                    : `฿${formatNumber(kpiMetrics.netProfit || 0)}`}
                        </span>
                        <span className='expense-kpi-sub'>
                            {kpiMetrics.profitState === 'loading' ? 'กำลังคำนวณ'
                                : kpiMetrics.profitState === 'error' ? 'ต้องมีทั้งรายรับและรายจ่ายจึงคำนวณได้'
                                    : 'รายรับ - รายจ่ายรวม'}
                        </span>
                    </div>
                    <div className='expense-kpi-icon-box profit'>📈</div>
                </div>

                <div className='expense-kpi-card category'>
                    <div className='expense-kpi-info'>
                        <span className='expense-kpi-label'>
                            <FontAwesomeIcon icon="chart-pie" style={{ color: '#D97706' }} /> หมวดหมู่ที่จ่ายมากสุด
                        </span>
                        <span className='expense-kpi-val' data-testid="kpi-top-category"
                            style={{ fontSize: '16px', color: kpiMetrics.expenseState === 'ready' ? '#D97706' : '#94A3B8' }}>
                            {kpiMetrics.expenseState === 'loading' ? 'กำลังโหลด…'
                                : kpiMetrics.expenseState === 'error' ? 'โหลดไม่สำเร็จ'
                                    : kpiMetrics.topCatName.length > 18 ? kpiMetrics.topCatName.slice(0, 18) + '...' : kpiMetrics.topCatName}
                        </span>
                        <span className='expense-kpi-sub'>
                            {kpiMetrics.expenseState === 'loading' ? 'กำลังดึงรายการค่าใช้จ่าย'
                                : kpiMetrics.expenseState === 'error' ? 'ลองรีเฟรชหน้านี้อีกครั้ง'
                                    : `฿${formatNumber(kpiMetrics.topCatAmount)}`}
                        </span>
                    </div>
                    <div className='expense-kpi-icon-box category'>🏷️</div>
                </div>
            </div>

            {/* Income & Expense Origin Info Banner */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                borderRadius: '12px',
                padding: '12px 18px',
                marginBottom: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                fontSize: '13px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>💡</span>
                    <span>
                        <strong style={{ color: 'var(--primary, #FF7A59)' }}>ที่มาของข้อมูล:</strong> "<strong>รายรับ (Revenue)</strong>" มาจากยอดขายสินค้าจริงที่ชำระเงินสำเร็จผ่านหน้าขายหน้าร้าน (<a onClick={() => navigate('/sale')} style={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', color: '#3B82F6' }}>/sale</a>) และบิลขาย (invoice) | "<strong>รายจ่าย (Expense)</strong>" มาจากรายการที่บันทึกในหน้านี้
                    </span>
                </div>
                <Button
                    type="primary"
                    size="small"
                    icon={<FontAwesomeIcon icon="chart-line" style={{ marginRight: '4px' }} />}
                    style={{
                        background: '#3B82F6',
                        borderColor: '#3B82F6',
                        borderRadius: '8px',
                        fontWeight: 600
                    }}
                    onClick={() => navigate('/report?tab=sales')}
                >
                    ดูรายงานยอดขายและสถิติเต็มรูปแบบ →
                </Button>
            </div>

            {/* Quick Filter Bar */}
            <div className='expense-filter-bar'>
                {/* Top Row: Left = Quick Date Buttons, Right = Category & Payment Dropdowns */}
                <div className='expense-filter-top-row'>
                    <div className='expense-period-btn-group'>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === 'all' ? 'active' : ''}`}
                            onClick={() => { setPeriod('all'); setStartDate(''); setEndDate(''); }}
                            data-testid="btn-period-all"
                        >
                            ทั้งหมด
                        </button>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === 'today' ? 'active' : ''}`}
                            onClick={() => { setPeriod('today'); setStartDate(''); setEndDate(''); }}
                            data-testid="btn-period-today"
                        >
                            วันนี้
                        </button>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === '7days' ? 'active' : ''}`}
                            onClick={() => { setPeriod('7days'); setStartDate(''); setEndDate(''); }}
                            data-testid="btn-period-7days"
                        >
                            7 วันล่าสุด
                        </button>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === 'this_month' ? 'active' : ''}`}
                            onClick={() => { setPeriod('this_month'); setStartDate(''); setEndDate(''); }}
                            data-testid="btn-period-this-month"
                        >
                            เดือนนี้
                        </button>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === 'last_month' ? 'active' : ''}`}
                            onClick={() => { setPeriod('last_month'); setStartDate(''); setEndDate(''); }}
                            data-testid="btn-period-last-month"
                        >
                            เดือนที่แล้ว
                        </button>
                        <button
                            type="button"
                            className={`expense-period-btn ${period === 'custom' ? 'active' : ''}`}
                            onClick={() => setPeriod('custom')}
                            data-testid="btn-period-custom"
                        >
                            📅 กำหนดช่วงวัน
                        </button>
                    </div>

                    <div className='expense-filter-top-right'>
                        <Select
                            value={categoryFilter}
                            onChange={(val) => setCategoryFilter(val)}
                            style={{ width: 170 }}
                            placeholder="กรองตามหมวดหมู่"
                        >
                            <Option value="all">📂 ทุกหมวดหมู่</Option>
                            {categories.map(c => (
                                <Option key={c.id} value={c.id}>
                                    {c.name}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            value={paymentFilter}
                            onChange={(val) => setPaymentFilter(val)}
                            style={{ width: 155 }}
                            placeholder="ช่องทางชำระ"
                        >
                            <Option value="all">💳 ทุกช่องทางชำระ</Option>
                            <Option value={1}>💵 เงินสด (Cash)</Option>
                            <Option value={2}>🏦 เงินโอน (Transfer)</Option>
                            <Option value={3}>📱 QR PromptPay</Option>
                            <Option value={4}>💳 บัตรเครดิต</Option>
                        </Select>
                    </div>
                </div>

                {/* Bottom Row: Left = Date Range Selector, Right = Action Buttons */}
                <div className='expense-filter-bottom-row'>
                    <div className='expense-filter-bottom-left'>
                        <DatePicker.RangePicker
                            placeholder={['วันที่เริ่มต้น', 'วันที่สิ้นสุด']}
                            format="YYYY-MM-DD"
                            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
                            onChange={(dates, dateStrings) => {
                                if (dates && dateStrings[0] && dateStrings[1]) {
                                    setStartDate(dateStrings[0]);
                                    setEndDate(dateStrings[1]);
                                    setPeriod('custom');
                                } else {
                                    setStartDate('');
                                    setEndDate('');
                                    setPeriod('all');
                                }
                            }}
                            style={{ borderRadius: '8px', minWidth: '240px' }}
                            data-testid="expense-date-range-picker"
                        />
                    </div>

                    <div className='expense-action-buttons'>
                        <Button
                            icon={<FontAwesomeIcon icon="tags" style={{ marginRight: '6px' }} />}
                            onClick={() => setIsCategoryModalOpen(true)}
                            style={{ borderRadius: '8px', fontWeight: 600 }}
                            data-testid="btn-manage-categories"
                        >
                            หมวดหมู่ค่าใช้จ่าย
                        </Button>
                        <Button
                            icon={<FontAwesomeIcon icon="file-excel" style={{ marginRight: '6px', color: '#10B981' }} />}
                            onClick={handleExportExcel}
                            style={{ borderRadius: '8px', fontWeight: 600 }}
                            data-testid="btn-export-excel"
                        >
                            ส่งออก Excel
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table View */}
            <CommonTableList
                page='expense'
                placeholder={placeholder_txt}
                columns={columns}
                data={filteredData}
                filterField={filterField}
                onAdd={() => onShowDetail('ADD')}
                hidePrint={false}
                onPrint={handlePrintExpenseReport}
            />

            {/* Detail Form Modal */}
            <CommonDetail
                width='680px'
                open={open}
                mode={mode}
                title={title}
                onClosed={onCloseDetail}
                onSaved={onSave}
                element={
                    <ExpenseDetail
                        expense={formData}
                        categories={categories}
                        paymentTypes={paymentTypes}
                        disabled={mode === 'VIEW'}
                        onChange={(updated) => setFormData(updated)}
                    />
                }
            />

            {/* Category Management Modal */}
            <ExpenseCategoryModal
                open={isCategoryModalOpen}
                categories={categories}
                onClose={() => setIsCategoryModalOpen(false)}
                onRefresh={loadData}
            />

            {/* Payment Voucher Print Modal */}
            <ExpenseVoucherPrint
                open={isVoucherModalOpen}
                expense={voucherData}
                onClose={() => setIsVoucherModalOpen(false)}
            />
        </div>
    );
};

export default Expense;
